import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const listingOversightSlice = createSlice({
    name: "listingOversight",
    initialState: {
        page: 0,
        pageCount: 0,
        total: 0,
        listData: null,
        statsData: null,
        detailData: null,
        flagSuccess: false,
        approveSuccess: false,
        rejectSuccess: false,
        removePhotoSuccess: false,
        removeSuccess: false,
        exporting: false,
        errorDetailData: null,
    },
    reducers: {
        doGetListingListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.total = action.payload.total_row;
        },
        doGetListingListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.total = 0;
        },
        doGetListingStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetListingStatsFail: (state, action) => {
            state.statsData = null;
        },
        doGetListingDetailSuccess: (state, action) => {
            state.detailData = action.payload.listing;
        },
        doGetListingDetailFail: (state, action) => {
            state.detailData = null;
        },
        doFlagListingSuccess: (state, action) => {
            state.flagSuccess = true;
        },
        doFlagListingFail: (state, action) => {
            state.flagSuccess = false;
            state.errorDetailData = action.payload;
        },
        doApproveListingSuccess: (state, action) => {
            state.approveSuccess = true;
        },
        doApproveListingFail: (state, action) => {
            state.approveSuccess = false;
            state.errorDetailData = action.payload;
        },
        doRejectListingSuccess: (state, action) => {
            state.rejectSuccess = true;
        },
        doRejectListingFail: (state, action) => {
            state.rejectSuccess = false;
            state.errorDetailData = action.payload;
        },
        doRemovePhotoSuccess: (state, action) => {
            state.removePhotoSuccess = true;
        },
        doRemovePhotoFail: (state, action) => {
            state.removePhotoSuccess = false;
            state.errorDetailData = action.payload;
        },
        doRemoveListingSuccess: (state, action) => {
            state.removeSuccess = true;
        },
        doRemoveListingFail: (state, action) => {
            state.removeSuccess = false;
            state.errorDetailData = action.payload;
        },
        setListingExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetListingOversightState: (state) => {
            state.flagSuccess = false;
            state.approveSuccess = false;
            state.rejectSuccess = false;
            state.removePhotoSuccess = false;
            state.removeSuccess = false;
            state.errorDetailData = null;
        },
        resetListingOversightDetailState: (state) => {
            state.detailData = null;
            state.flagSuccess = false;
            state.approveSuccess = false;
            state.rejectSuccess = false;
            state.removePhotoSuccess = false;
            state.removeSuccess = false;
            state.errorDetailData = null;
        },
    },
});

// Builds the shared query string used by both the list and export endpoints.
const buildListingQuery = (param = {}) => {
    const q = [];
    if (param.page != null) q.push(`page=${param.page}`);
    if (param.limit != null) q.push(`size=${param.limit}`);
    if (param.keyword) q.push(`keyword=${encodeURIComponent(param.keyword)}`);
    if (param.status) q.push(`status=${param.status}`);
    if (param.room_type) q.push(`room_type=${param.room_type}`);
    if (param.host_id) q.push(`host_id=${param.host_id}`);
    if (param.is_flagged != null) q.push(`is_flagged=${param.is_flagged}`);
    if (param.filter && param.filter !== "all") q.push(`filter=${param.filter}`);
    if (param.created_from) q.push(`created_from=${param.created_from}`);
    if (param.created_to) q.push(`created_to=${param.created_to}`);
    if (param.sort_by) q.push(`sort_by=${param.sort_by}`);
    return q.length ? `?${q.join("&")}` : "";
};

export const doGetListingList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/listing-oversight${buildListingQuery(param)}`);
        dispatch(doGetListingListSuccess(response.data));
    } catch (error) {
        dispatch(doGetListingListFail(error.response));
    }
};

export const doRemoveListing = (listingId) => async (dispatch) => {
    try {
        const response = await axios.delete(`/api/admin/listing-oversight/${listingId}`);
        dispatch(doRemoveListingSuccess(response.data));
    } catch (error) {
        dispatch(doRemoveListingFail(error.response));
    }
};

// Download the filtered listings as a CSV file (streamed as a blob).
export const doExportListing = (param) => async (dispatch) => {
    try {
        dispatch(setListingExporting(true));
        const url = `/api/admin/listing-oversight/export${buildListingQuery({ ...param, page: undefined, limit: undefined })}`;
        const response = await axios.get(url, { responseType: "blob" });

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const ym = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}`;
        link.setAttribute("download", `listings_${ym}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setListingExporting(false));
    }
};

export const doGetListingStats = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/listing-oversight/stats`);
        dispatch(doGetListingStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetListingStatsFail(error.response));
    }
};

export const doGetListingDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/listing-oversight/${param.listing_id}`;
        const response = await axios.get(url);
        dispatch(doGetListingDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetListingDetailFail(error.response));
    }
};

export const doFlagListing = (listingId, is_flagged, flag_reason) => async (dispatch) => {
    try {
        const url = `/api/admin/listing-oversight/${listingId}/flag`;
        const response = await axios.patch(url, { is_flagged, flag_reason });
        dispatch(doFlagListingSuccess(response.data));
    } catch (error) {
        dispatch(doFlagListingFail(error.response));
    }
};

export const doApproveListing = (listingId) => async (dispatch) => {
    try {
        const url = `/api/admin/listing-oversight/${listingId}/approve`;
        const response = await axios.patch(url);
        dispatch(doApproveListingSuccess(response.data));
    } catch (error) {
        dispatch(doApproveListingFail(error.response));
    }
};

export const doRejectListing = (listingId, reject_reason) => async (dispatch) => {
    try {
        const url = `/api/admin/listing-oversight/${listingId}/reject`;
        const response = await axios.patch(url, { reject_reason });
        dispatch(doRejectListingSuccess(response.data));
    } catch (error) {
        dispatch(doRejectListingFail(error.response));
    }
};

export const doRemovePhoto = (listingId, photoId) => async (dispatch) => {
    try {
        const url = `/api/admin/listing-oversight/${listingId}/photos/${photoId}`;
        const response = await axios.delete(url);
        dispatch(doRemovePhotoSuccess(response.data));
    } catch (error) {
        dispatch(doRemovePhotoFail(error.response));
    }
};

export const {
    doGetListingListSuccess,
    doGetListingListFail,
    doGetListingStatsSuccess,
    doGetListingStatsFail,
    doGetListingDetailSuccess,
    doGetListingDetailFail,
    doFlagListingSuccess,
    doFlagListingFail,
    doApproveListingSuccess,
    doApproveListingFail,
    doRejectListingSuccess,
    doRejectListingFail,
    doRemovePhotoSuccess,
    doRemovePhotoFail,
    doRemoveListingSuccess,
    doRemoveListingFail,
    setListingExporting,
    resetListingOversightState,
    resetListingOversightDetailState,
} = listingOversightSlice.actions;

export const getListingList = (state) => state.listingOversight?.listData;
export const getListingStats = (state) => state.listingOversight?.statsData;
export const getListingListPage = (state) => state.listingOversight?.page;
export const getListingListPageCount = (state) => state.listingOversight?.pageCount;
export const getListingListTotal = (state) => state.listingOversight?.total;
export const getListingDetail = (state) => state.listingOversight?.detailData;
export const getFlagSuccess = (state) => state.listingOversight?.flagSuccess;
export const getApproveSuccess = (state) => state.listingOversight?.approveSuccess;
export const getRejectSuccess = (state) => state.listingOversight?.rejectSuccess;
export const getRemovePhotoSuccess = (state) => state.listingOversight?.removePhotoSuccess;
export const getRemoveSuccess = (state) => state.listingOversight?.removeSuccess;
export const getListingExporting = (state) => state.listingOversight?.exporting;
export const getListingErrorDetailData = (state) => state.listingOversight?.errorDetailData;

export default listingOversightSlice.reducer;
